
import { memo, useMemo, useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Grid, Heading, Text, Image, Input, InputGroup,
    InputLeftElement, IconButton, Button, Skeleton, SkeletonCircle,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { get_brands } from 'redux/actions/productActions';
import useDebounce from 'hooks/useDebounce';

/* ─────────────────────────────────────────────────────────────
   AllBrands page — /dashboard/brands
   Mirrors AllVendors structure:
     - Header with back icon
     - Title with count
     - Search input
     - Letter-grouped alphabetical sections
     - Grid of brand cards
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';
const FONT      = "'Bricolage Grotesque', sans-serif";

const SEARCH_DEBOUNCE_MS = 250;

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);
const TagIcon = (props) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M21.41 11.58L12.41 2.58C12.04 2.21 11.53 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.53 2.21 12.04 2.59 12.42L11.59 21.42C11.95 21.79 12.44 22 13 22C13.56 22 14.05 21.79 14.41 21.41L21.41 14.41C21.79 14.05 22 13.56 22 13C22 12.44 21.78 11.95 21.41 11.58ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5Z" />
    </svg>
);
const ChevronLeftIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const toInitial = (name) => (name?.[0] ?? '?').toUpperCase();

/* ─────────────────────────────────────────────────────────────
   BRAND AVATAR
   ───────────────────────────────────────────────────────────── */
const BrandAvatar = memo(function BrandAvatar({ brand, size, fontSize }) {
    const initial = toInitial(brand?.name);

    return (
        <Flex
            w={size}
            h={size}
            borderRadius="full"
            bg={brand?.image ? SURFACE : CYAN}
            color={NAVY}
            align="center"
            justify="center"
            fontWeight="800"
            fontSize={fontSize}
            fontFamily={FONT}
            overflow="hidden"
            border="1px solid"
            borderColor={BORDER}
            flexShrink={0}
        >
            {brand?.image ? (
                <Image
                    src={brand.image}
                    alt={brand.name}
                    w="full"
                    h="full"
                    objectFit="cover"
                    fallback={
                        <Flex w="full" h="full" align="center" justify="center" bg={CYAN}>
                            {initial}
                        </Flex>
                    }
                />
            ) : (
                initial
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   BRAND CARD
   ───────────────────────────────────────────────────────────── */
const BrandCard = memo(function BrandCard({ brand, onClick }) {
    return (
        <Flex
            direction="column"
            align="center"
            gap="8px"
            p={{ base: '10px 6px', md: '14px 10px' }}
            borderRadius="10px"
            cursor="pointer"
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            transition="all 0.15s ease"
            boxShadow={SHADOW_SM}
            minW="0"
            w="full"
            _hover={{
                borderColor: NAVY,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(27,59,95,0.10)',
            }}
            role="button"
            tabIndex={0}
            aria-label={`View ${brand?.name} products`}
        >
            <BrandAvatar
                brand={brand}
                size={{ base: '44px', md: '56px' }}
                fontSize={{ base: '15px', md: '18px' }}
            />
            <Box textAlign="center" minW="0" w="full">
                <Text
                    fontSize={{ base: '10px', md: '11.5px' }}
                    fontWeight="700"
                    color={INK}
                    noOfLines={2}
                    lineHeight="1.25"
                    minH={{ base: '25px', md: '29px' }}
                    fontFamily={FONT}
                >
                    {brand?.name}
                </Text>
            </Box>
        </Flex>
    );
});

const BrandCardSkeleton = memo(function BrandCardSkeleton() {
    return (
        <Box
            p={{ base: '10px 6px', md: '14px 10px' }}
            borderRadius="10px"
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            minW="0"
            w="full"
        >
            <Flex direction="column" align="center" gap="8px">
                <SkeletonCircle size={{ base: '44px', md: '56px' }} startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2.5" w="80%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   LETTER DIVIDER
   ───────────────────────────────────────────────────────────── */
const LetterDivider = memo(function LetterDivider({ letter, count }) {
    return (
        <Flex
            align="center"
            gap="10px"
            mb="10px"
            w="full"
            minW="0"
        >
            <Flex
                w="26px"
                h="26px"
                borderRadius="7px"
                bg={NAVY}
                color="white"
                align="center"
                justify="center"
                fontWeight="800"
                fontSize="12px"
                fontFamily={FONT}
                flexShrink={0}
            >
                {letter}
            </Flex>
            <Box flex="1" minW="0" h="1px" bg={BORDER} />
            <Text fontSize="10px" color={FAINT} fontWeight="700" flexShrink={0}>
                {count}
            </Text>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */
const EmptyState = ({ hasSearch, onClearSearch }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py={{ base: '40px', md: '60px' }}
        px="20px"
        textAlign="center"
        gap="12px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="12px"
    >
        <Flex w="56px" h="56px" borderRadius="full" bg={SURFACE_ALT} color={FAINT} align="center" justify="center">
            <TagIcon />
        </Flex>
        <Box>
            <Heading as="h3" fontSize="14px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                {hasSearch ? 'No brands match your search' : 'No brands available yet'}
            </Heading>
            <Text fontSize="11.5px" color={MUTED}>
                {hasSearch ? 'Try a different search term.' : 'Check back soon — new brands join regularly.'}
            </Text>
        </Box>
        {hasSearch && (
            <Button
                onClick={onClearSearch}
                size="sm"
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                fontSize="11.5px"
                fontWeight="700"
                borderRadius="8px"
                h="34px"
                px="12px"
            >
                Clear search
            </Button>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
const Brands = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const brands = useSelector((s) => s.productReducer?.brands);

    useEffect(() => {
        if (!Array.isArray(brands) || brands.length === 0) {
            dispatch(get_brands()).finally?.(() => {
                if (typeof window !== 'undefined') {
                    window.__obanaBrandsFetched = true;
                }
            });
        }
    }, [dispatch, brands]);

    const isLoading =
        brands == null ||
        (Array.isArray(brands) && brands.length === 0 && !window.__obanaBrandsFetched);

    const [searchValue, setSearchValue] = useState('');
    const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);

    const allBrands = useMemo(() => {
        if (!Array.isArray(brands)) return [];
        return brands.filter((b) => b && b.name);
    }, [brands]);

    const filteredBrands = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        if (!q) return allBrands;
        return allBrands.filter((b) => b.name.toLowerCase().includes(q));
    }, [allBrands, debouncedSearch]);

    const groupedBrands = useMemo(() => {
        const groups = {};
        filteredBrands.forEach((b) => {
            const letter = (b.name?.[0] ?? '#').toUpperCase();
            const key = /[A-Z]/.test(letter) ? letter : '#';
            if (!groups[key]) groups[key] = [];
            groups[key].push(b);
        });
        return Object.keys(groups)
            .sort((a, b) => {
                if (a === '#') return 1;
                if (b === '#') return -1;
                return a.localeCompare(b);
            })
            .map((letter) => ({ letter, brands: groups[letter] }));
    }, [filteredBrands]);

    const goToBrand = useCallback(
        (brand) => {
            if (!brand?.brand_id) return;
            navigate(`/dashboard/brands/${brand.brand_id}`);
        },
        [navigate]
    );

    const hasSearch = debouncedSearch.trim().length > 0;
    const noResults = !isLoading && filteredBrands.length === 0;

    return (
        <Box
            bg={SURFACE_ALT}
            minH="100vh"
            fontFamily={FONT}
            px={{ base: '12px', md: '24px' }}
            py={{ base: '12px', md: '20px' }}
            w="full"
            maxW="100vw"
            overflowX="hidden"
        >
            <Box maxW="1280px" mx="auto" w="full" minW="0">
                {/* HEADER */}
                <Flex align="center" gap="8px" mb="14px">
                    <IconButton
                        aria-label="Back to dashboard"
                        icon={<ChevronLeftIcon />}
                        onClick={() => navigate('/dashboard')}
                        variant="ghost"
                        size="sm"
                        color={MUTED}
                        _hover={{ bg: SURFACE, color: NAVY }}
                        h="32px"
                        minW="32px"
                        flexShrink={0}
                    />
                    <Box flex="1" minW="0">
                        <Heading
                            as="h1"
                            fontSize={{ base: '18px', md: '24px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.2"
                            noOfLines={1}
                        >
                            All Brands
                        </Heading>
                        <Text fontSize="11px" color={MUTED} mt="2px" noOfLines={1}>
                            {isLoading
                                ? 'Loading…'
                                : hasSearch
                                ? `${filteredBrands.length} of ${allBrands.length} brands`
                                : `${allBrands.length} brand${allBrands.length === 1 ? '' : 's'} available`}
                        </Text>
                    </Box>
                </Flex>

                {/* SEARCH */}
                <Box mb="20px" w="full" minW="0">
                    <InputGroup>
                        <InputLeftElement pointerEvents="none" h="42px">
                            <Box color={FAINT}><SearchIcon /></Box>
                        </InputLeftElement>
                        <Input
                            type="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder="Search brands…"
                            h="42px"
                            pl="40px"
                            pr={searchValue ? '40px' : '14px'}
                            fontSize="13px"
                            bg={SURFACE}
                            border="1px solid"
                            borderColor={BORDER}
                            borderRadius="10px"
                            fontFamily={FONT}
                            _placeholder={{ color: FAINT }}
                            _hover={{ borderColor: '#CBD5E0' }}
                            _focus={{
                                borderColor: NAVY,
                                boxShadow: `0 0 0 3px ${CYAN}`,
                                bg: SURFACE,
                            }}
                        />
                        {searchValue && (
                            <Box
                                position="absolute"
                                right="6px"
                                top="50%"
                                transform="translateY(-50%)"
                                zIndex={2}
                            >
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<CloseIcon />}
                                    onClick={() => setSearchValue('')}
                                    variant="ghost"
                                    size="xs"
                                    h="28px"
                                    minW="28px"
                                    color={FAINT}
                                    _hover={{ color: INK, bg: 'transparent' }}
                                />
                            </Box>
                        )}
                    </InputGroup>
                </Box>

                {/* BODY */}
                {isLoading ? (
                    <Grid
                        templateColumns={{
                            base: 'repeat(3, 1fr)',
                            sm: 'repeat(4, 1fr)',
                            md: 'repeat(5, 1fr)',
                            lg: 'repeat(6, 1fr)',
                            xl: 'repeat(8, 1fr)',
                        }}
                        gap={{ base: '8px', md: '10px' }}
                        w="full"
                    >
                        {Array.from({ length: 18 }).map((_, i) => (
                            <BrandCardSkeleton key={i} />
                        ))}
                    </Grid>
                ) : noResults ? (
                    <EmptyState hasSearch={hasSearch} onClearSearch={() => setSearchValue('')} />
                ) : (
                    <Flex direction="column" gap={{ base: '16px', md: '24px' }}>
                        {groupedBrands.map(({ letter, brands: brandsInLetter }) => (
                            <Box key={letter} w="full" minW="0">
                                <LetterDivider letter={letter} count={brandsInLetter.length} />
                                <Grid
                                    templateColumns={{
                                        base: 'repeat(3, 1fr)',
                                        sm: 'repeat(4, 1fr)',
                                        md: 'repeat(5, 1fr)',
                                        lg: 'repeat(6, 1fr)',
                                        xl: 'repeat(8, 1fr)',
                                    }}
                                    gap={{ base: '8px', md: '10px' }}
                                    w="full"
                                >
                                    {brandsInLetter.map((b) => (
                                        <BrandCard
                                            key={b.brand_id ?? b.name}
                                            brand={b}
                                            onClick={() => goToBrand(b)}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Flex>
                )}
            </Box>
        </Box>
    );
};

export default Brands;